import { createContext, useContext } from "react";

/** Who the portal is showing, for anything below the shell that needs to say so. */
export interface CarrierIdentity {
  carrierIdentifier: string;
  carrierName: string;
}

/**
 * The signed-in carrier, available to every page.
 *
 * A context rather than props through the router: the identity is fixed for the
 * whole session, and the alternative is threading two strings through every
 * route. Pages use it to say whose numbers they are showing — a carrier reading
 * "Win Rate" has no way to tell it means theirs and not FreightSimple's.
 */
const CarrierIdentityContext = createContext<CarrierIdentity | undefined>(
  undefined,
);

export const CarrierIdentityProvider = CarrierIdentityContext.Provider;

/**
 * Throws outside the provider rather than returning undefined. Everything below
 * the shell is behind authentication, so an absent identity is a wiring mistake,
 * and a silent undefined would surface as a page titled with a blank name.
 */
export function useCarrierIdentity(): CarrierIdentity {
  const identity = useContext(CarrierIdentityContext);
  if (identity === undefined) {
    throw new Error("useCarrierIdentity must be used inside CarrierShell");
  }
  return identity;
}
